import Button from 'components/UI/Buttons/Button'
import Typography from 'components/UI/Typography'
import { useAppDispatch, useAppSelector } from 'hooks'
import useLogout from 'hooks/useLogout'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useLazyGetApplicationStatusQuery } from 'redux/api'
import { useCancelCreditMutation } from 'redux/api/creditApi'
import { useLazyGetUserQuery } from 'redux/api/leadsApi'
import {
  useSaveAdditionalDocumentsMutation,
  useSkipUpgradeConditionsMutation,
  useUploadAdditionalDocumentsMutation
} from 'redux/api/scoringApi'
import { getCreditParamsSelector, getStatusSelector } from 'redux/store/slices/statusSlice'
import { getActiveCreditApplication, getCurrentFlowId } from 'redux/store/slices/userSlice'
import { getNotificationsSelector, removeStepNotificationsData } from 'redux/store/slices/wsSlice'
import { ApplicationEvents } from 'share/enums/events'
import { FLOW_STEPS } from 'share/types'
import { UpgradeConditionsFormFiles } from 'share/types/model/files'
import tw from 'twin.macro'
import { getWsStepStatus, getWsStepStatuses } from 'utils'
import Emitter from 'utils/emitter'
import { getRouteByStatus } from 'utils/routing/routing-helper'

import UpgradeConditionsForm from './UpgradeConditionsForm'
import UpgradeConditionState from './UpgradeConditionState'

type UpgradeConditionsProps = {
  onUpgradeSkipOrUpdateData?: () => void
}

const formInitState: {
  [key in UpgradeConditionsFormFiles]: {
    maxFiles: number
    files: Array<File>
  }
} = {
  BANK_STATEMENT: {
    maxFiles: 1,
    files: []
  },
  PARSE_SIMPLIFIED: {
    maxFiles: 1,
    files: []
  },
  TURNOVER_BALANCE: {
    maxFiles: 1,
    files: []
  }
}

const UpgradeOwnLoanConditions = ({ onUpgradeSkipOrUpdateData }: UpgradeConditionsProps) => {
  // Tools
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Custom hooks
  const logout = useLogout()

  // Selectors
  const { id: creditID, improve_condition_with_addition_document } = useAppSelector(getActiveCreditApplication)
  const currentFlowId = useAppSelector(getCurrentFlowId)
  const { current_step } = useAppSelector(getStatusSelector)
  const { GET_DOCS, PARSE_ADD_DOCS, SCORING } = useAppSelector(getNotificationsSelector)
  const { loan_amount } = useAppSelector(getCreditParamsSelector)

  // Mutations
  const [saveAdditionalDocuments, { isLoading: isSaveAdditionalDocumentsLoading }] =
    useSaveAdditionalDocumentsMutation()
  const [skipUpgradeConditions, { isLoading: isSkipUpgradeConditionsLoading }] = useSkipUpgradeConditionsMutation()
  const [fetchStatus, { isLoading: isStatusLoading }] = useLazyGetApplicationStatusQuery()
  const [fetchUser] = useLazyGetUserQuery()
  const [cancelCredit, { isLoading: isCancelCreditLoading }] = useCancelCreditMutation()
  const [uploadAdditionalDocuments, { error, isLoading: isUploadDocumentsLoading }] =
    useUploadAdditionalDocumentsMutation()

  const uploadAddDocsError = error as {
    data: { parse_simplified: string; bank_statement: string; turnover_balance: string }
  }

  // State
  const [form, setForm] = useState<{
    [key in UpgradeConditionsFormFiles]: {
      maxFiles: number
      files: Array<File>
    }
  }>(formInitState)
  const [isError, setIsError] = useState('')

  // Computed values
  const isOneFileAdded = Object.values(form).some(formField => formField.files.length !== 0)

  // TODO: Подумать над хэндлингом статусов так как текущее не ахти
  const isAnyCalcIncomeStepInProgress = useMemo(
    () =>
      getWsStepStatuses(GET_DOCS, PARSE_ADD_DOCS, SCORING) === 'IN_PROGRESS' ||
      (current_step?.step === FLOW_STEPS.GET_DOCS && current_step.status === 'IN_PROGRESS') ||
      (current_step?.step === FLOW_STEPS.PARSE_ADD_DOCS && current_step.status === 'IN_PROGRESS') ||
      (current_step?.step === FLOW_STEPS.SCORING && current_step.status === 'IN_PROGRESS'),
    [GET_DOCS, PARSE_ADD_DOCS, SCORING, current_step?.status, current_step?.step]
  )

  const isAnyCalcIncomeStepInRetry = useMemo(
    () =>
      getWsStepStatuses(GET_DOCS, PARSE_ADD_DOCS, SCORING) === 'FAILED' ||
      (current_step?.step === FLOW_STEPS.GET_DOCS && current_step.status === 'FAILED') ||
      (current_step?.step === FLOW_STEPS.PARSE_ADD_DOCS && current_step.status === 'FAILED'),
    [GET_DOCS, PARSE_ADD_DOCS, SCORING, current_step?.status, current_step?.step]
  )

  // Handlers
  const fetchFlowIdsAndNavigate = async () => {
    const { current_flow_id } = await fetchUser().unwrap()
    const response = await fetchStatus(current_flow_id).unwrap()
    navigate(getRouteByStatus(response) as string)
  }

  const handleFileChange = ({ name, file }: { name: string; file?: File }) => {
    setForm(prevState => {
      return {
        ...prevState,
        [name]: {
          ...prevState[name as UpgradeConditionsFormFiles],
          files: [...prevState[name as UpgradeConditionsFormFiles].files, file]
        }
      }
    })
  }

  const handleFileRemove = ({ name, file }: { name: string; file?: File }) => {
    if (!file) return
    setForm(prevState => {
      const copyArr = prevState[name as UpgradeConditionsFormFiles].files.slice()
      copyArr.splice(copyArr.indexOf(file), 1)

      return {
        ...prevState,
        [name]: {
          ...prevState[name as UpgradeConditionsFormFiles],
          files: copyArr
        }
      }
    })
  }

  const handleDocumentFormSubmit = useCallback(async () => {
    try {
      // Clear prev upgrade results from redux to show loaders properly in ImproveConditionsLoader
      dispatch(removeStepNotificationsData([FLOW_STEPS.SCORING, FLOW_STEPS.GET_DOCS, FLOW_STEPS.PARSE_ADD_DOCS]))

      if (loan_amount && +loan_amount !== 0 && !isOneFileAdded) {
        await skipUpgradeConditions({ flow_id: currentFlowId as string }).unwrap()
        await fetchFlowIdsAndNavigate()
        onUpgradeSkipOrUpdateData?.()
        return
      }
      const formData = new FormData()
      Object.keys(form).map(key => {
        form[key as UpgradeConditionsFormFiles].files.map(file => {
          formData.append(key.toLowerCase(), file)
        })
      })
      const res = await uploadAdditionalDocuments({
        flow_id: currentFlowId as string,
        formData
      }).unwrap()
      await saveAdditionalDocuments({
        id: currentFlowId as string,
        body: {
          files: Object.values(res).map(id => ({
            id
          }))
        }
      }).unwrap()

      await fetchFlowIdsAndNavigate()
    } catch (error) {
      console.log(error)
      error && setIsError('Размер файла превышен')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFlowId, form, isOneFileAdded, loan_amount])

  const handleCancelCredit = async () => {
    try {
      await cancelCredit(creditID as string).unwrap()
      logout()
    } catch (error) {
      console.log(error)
    }
  }

  const clearFormFiles = () => {
    setForm(formInitState)
  }

  // Effects
  useEffect(() => {
    if (improve_condition_with_addition_document === null) {
      dispatch(removeStepNotificationsData([FLOW_STEPS.SCORING, FLOW_STEPS.GET_DOCS, FLOW_STEPS.PARSE_ADD_DOCS]))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [improve_condition_with_addition_document])

  useEffect(() => {
    if (isAnyCalcIncomeStepInProgress) {
      setTimeout(() => {
        clearFormFiles()
      }, 400)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnyCalcIncomeStepInProgress])

  useEffect(() => {
    const checkStatus = async () => {
      if (getWsStepStatus(PARSE_ADD_DOCS) === 'RETRY') {
        Emitter.emit(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE)
      }
      if (getWsStepStatus(SCORING) === 'DONE') {
        onUpgradeSkipOrUpdateData?.()
        Emitter.emit(ApplicationEvents.FETCH_FLOW_IDS_AND_NAVIGATE)
      }
    }
    checkStatus()
  }, [PARSE_ADD_DOCS, SCORING])

  return (
    <Fragment>
      <UpgradeConditionsForm
        form={form}
        errors={uploadAddDocsError?.data}
        isLoading={isAnyCalcIncomeStepInProgress}
        isStepInError={isAnyCalcIncomeStepInRetry}
        onFileChange={handleFileChange}
        onFileRemove={handleFileRemove}
      />
      {isError && (
        <Typography text twStyle={tw`text-error pl-5 pt-5`}>
          {isError}
        </Typography>
      )}
      <UpgradeConditionState stepStatus={isAnyCalcIncomeStepInProgress} />
      <div tw='flex flex-col px-4 gap-y-4 items-center mt-5 md:flex-row md:px-0 md:gap-x-4'>
        <Button
          variant='text'
          twStyle={tw`order-2 md:order-1 flex-shrink-0`}
          disabled={
            isSaveAdditionalDocumentsLoading ||
            isSkipUpgradeConditionsLoading ||
            isStatusLoading ||
            isCancelCreditLoading
          }
          onClick={handleCancelCredit}
        >
          <Typography variant='body' weight='bold' twStyle={tw`text-red-light lg:whitespace-nowrap font-medium`}>
            {t('flow-cancel-apl')}
          </Typography>
        </Button>
        <Button
          variant='secondary'
          twStyle={tw`md:self-end order-1 md:order-2`}
          onClick={handleDocumentFormSubmit}
          loading={
            isSaveAdditionalDocumentsLoading ||
            isSkipUpgradeConditionsLoading ||
            isStatusLoading ||
            isCancelCreditLoading ||
            isUploadDocumentsLoading
          }
          disabled={
            (((loan_amount && +loan_amount === 0) as boolean | undefined) && !isOneFileAdded) ||
            isAnyCalcIncomeStepInProgress
          }
        >
          <Typography variant='body' weight='bold' twStyle={tw`text-white`}>
            {loan_amount && +loan_amount !== 0 && !isOneFileAdded ? t('flow-pass-single') : t('flow-recalc')}
          </Typography>
        </Button>
      </div>
    </Fragment>
  )
}

export default UpgradeOwnLoanConditions
